import React from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";

import ApiError from "common/ApiError";
import { app } from "js/namespaces";
import { ds } from "js/core/models/dataService";
import log from "js/core/logger";
import db from "js/db";
import Api from "js/core/api";
import { withFirebaseAuth } from "js/react/views/Auth/FirebaseAuthContext";
import { withFirebaseUser } from "js/react/views/Auth/FirebaseUserContext";
import { Authenticate } from "js/react/views/Auth/Authenticate";
import { Presentation, PresentationPrivacyType } from "js/core/models/presentation";
import { Slide } from "js/core/models/slide";
import { User } from "js/core/models/user";
import ErrorMessage from "js/app/ErrorMessage";
import { IntegratorController } from "common/integrators/IntegratorController";

import { getAnalytics, AnalyticsContextType } from "../helpers/analytics";
import PlayerContext from "js/react/views/Player/Context/PlayerContext";
import { BEAUTIFUL_WORKSPACE_ID } from "common/constants";

let importPromise;
function importModules() {
    if (!importPromise) {
        importPromise = Promise.all([
            import(/* webpackMode: "lazy" */ "js/core/preload"),
            import(/* webpackMode: "lazy" */ "js/elements/elementImports")
        ]);
    }

    return importPromise;
}

const AuthContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #F8FAFC;
`;

class PlayerAppContext extends PlayerContext {
    constructor() {
        super();

        this.state = {
            status: "loading",
            errorMessage: null
        };
    }

    componentDidMount() {
        window.isPlayer = true;

        IntegratorController.initApp()
            .then(() => this.load());
    }

    componentWillUnmount() {
        window.isPlayer = false;
    }

    handleCopyDeck = async ({ presentation, analytics, isLoggedIn }) => {
        if (!isLoggedIn) {
            window.location = `/copyDeck/${presentation.id}?source=player`;
            return;
        }

        try {
            const { url } = await Api.copyPresentation.post({
                presentationId: presentation.id,
                workspaceId: localStorage.getItem(BEAUTIFUL_WORKSPACE_ID)
            });

            analytics.trackUsePresentationAsTemplate();

            window.location = url;
        } catch (err) {
            log.error(err);
        }
    }

    async load() {
        const { auth, firebaseUser, match: { params: { id } } } = this.props;

        this.setState({ status: "loading" });

        try {
            await Promise.all([
                importModules(),
                (async () => {
                    const playerContext = await Api.playerContext.get({ id });
                    const {
                        presentation: presentationData,
                        showBranding,
                        isCreatorOldBasicUser,
                        showGetDeckUI,
                        canEditPresentation,
                        creator,
                        isCollaborator,
                        slug
                    } = playerContext;
                    const isEmbedded = window.self !== window.top && !window.Cypress && !window.forceEmbedded && !app.isConstrained;

                    if (!presentationData.public || presentationData.secured) {
                        history.replaceState({}, `Beautiful.ai - ${presentationData.name}`, `/player/${id}/${slug}`);
                    }

                    if (!app.user && firebaseUser) {
                        // Sign into all Firebase app instances
                        await db.updateCurrentUser(firebaseUser);

                        // Load the User model
                        app.user = new User({ id: firebaseUser.uid }, { autoLoad: false });
                        if (!ds.hasBeenSetup) {
                            ds.dummySetup();
                        }
                        await app.user.load();
                    } else if (!ds.hasBeenSetup) {
                        ds.dummySetup();
                    }

                    // Initialize dictionary
                    if (!app.dictionary) {
                        app.dictionary = {
                            check: () => true
                        };
                    }

                    // Load presentation model
                    const presentation = new Presentation({ ...presentationData }, { disconnected: true });

                    // Set selection
                    ds.selection.presentation = presentation;

                    // Note: slides won't be preloaded, the player will take care of loading them when needed
                    const slides = presentation.getSips()
                        .map(slideId => new Slide({ id: slideId }, { presentation, autoLoad: false }))
                        .reduce((slides, slide) => ({ ...slides, [slide.id]: slide }), {});

                    // Prefetching skipped slide ids (so we can correctly calculate slides count w/o having to preload all slide models)
                    const skippedSlideIds = [];
                    await Promise.all(
                        Object.keys(slides).map(slideId =>
                            db("slides").child(slideId).child("isSkipped").once("value")
                                .then(snap => snap.val())
                                .then(isSkipped => {
                                    if (isSkipped) {
                                        skippedSlideIds.push(slideId);
                                    }
                                })
                        )
                    );

                    let { isMobileOrTablet } = this.contextData;

                    const allowPdfDownload = (
                        presentation.get("allowPdfDownload") &&
                        presentation.getPrivacySetting() !== PresentationPrivacyType.PRIVATE
                    );

                    const allowSwitchPresentation = (
                        !!app.integrator &&
                        canEditPresentation
                    );

                    const allowEditPresentation = (
                        !isMobileOrTablet &&
                        canEditPresentation
                    );

                    let analytics = getAnalytics(AnalyticsContextType.APP, isEmbedded, firebaseUser, isCollaborator, presentation);
                    let isLoggedIn = firebaseUser && !firebaseUser.isAnonymous;

                    this.contextData = {
                        ...this.contextData,
                        isLoggedIn,
                        presentation,
                        slides,
                        skippedSlideIds,
                        showBranding,
                        isCreatorOldBasicUser,
                        allowCopyLink: true,
                        onCopyDeck: showGetDeckUI && (() => this.handleCopyDeck({ presentation, analytics, isLoggedIn })),
                        allowSocialSharing: presentation.get("showSocialSharing"),
                        onSwitchPresentation: allowSwitchPresentation && (() => app.integrator.showPresentationList()),
                        onEditPresentation: allowEditPresentation && (slideIndex => window.open(`/${presentation.id}/${slideIndex + 1}`, "_blank")),
                        onDownloadPdf: allowPdfDownload && (() => presentation.downloadExport("pdf", "local")),
                        creator,
                        isPresenterAvailable: !isEmbedded && canEditPresentation,
                        presenterUrl: "/presenter",
                        presenterWindowTarget: "_blank",
                        analytics
                    };
                })()
            ]);

            // Load theme
            await app.themeManager.loadTheme(this.contextData.presentation);

            this.setState({ status: "loaded" });
        } catch (err) {
            if (err instanceof ApiError && [403, 401].includes(err.status) && !firebaseUser) {
                // If the presentation is forbidden, prompt the user to log in
                const unsubscribe = auth.onAuthStateChanged(user => {
                    if (user) {
                        unsubscribe();
                        this.load();
                    }
                });

                this.setState({ status: "auth" });
                return;
            }

            if (window.parent !== window) {
                log.info("[PlayerContext] attempting to clear integrator state...");
                // We cannot access the parent on a crossorigin domain, so we try/catch to detect this.
                try {
                    const baiIntegrator = window.parent.baiIntegrator;
                    if (baiIntegrator) {
                        baiIntegrator.clearState();
                        log.info("[PlayerContext] cleared integrator state");
                    }
                } catch (err) {
                    log.error("[PlayerContext] unable to access upper iframe");
                    log.error(err);
                }
            }

            log.error(err);

            this.setState({
                status: "error",
                errorMessage: err.message
            });
        }
    }

    render() {
        const { status, errorMessage } = this.state;

        if (status === "loading") {
            return null;
        }

        if (status === "auth") {
            return <AuthContainer><Authenticate defaultPage="signIn" /></AuthContainer>;
        }

        if (status === "error") {
            return <ErrorMessage message={errorMessage} />;
        }

        return this.renderChildrenWithContext();
    }
}

export default withFirebaseAuth(withFirebaseUser(withRouter(PlayerAppContext)));
